import { ButtonsGrid, RaisedButton } from '@eduact/ed-components'
import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { WalletContext } from '../../../WalletContext'
import { useGetMethodRatesQuery } from '@Features/Dashboard/DashboardSlice/Dashboard.slice'
import { ProccessRechargeCallback } from '../../WalletTabs'
import { Typography } from '@eduact/ed-system'
import { useTranslation } from 'react-i18next'
import NumberFormatter from '@Utilities/NumberFormatter'
import { TabBody } from '../Tabs.styled'
import { layout, LayoutProps } from 'styled-system'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { useNavigate } from 'react-router-dom'
import { manualVcApi, manualVcBase, ManualVcQuote } from '@Features/ManualVc/ManualVc.api'
import { prepareManualDraft } from '@Features/ManualVc/ManualVcDraft'

const paymentValues = [50, 75, 80, 100, 140]
const ValuesTab: React.FC<{ proccessRecharge: ProccessRechargeCallback }> = ({ proccessRecharge }) => {
	const { walletIndex, rechargeAmount, provider, setRechargeAmount, setWalletIndex, closeWallet } = useContext(WalletContext)
	const manual = provider?.provider === 'manual_vc'
	const { data } = useGetMethodRatesQuery(manual ? undefined : provider?.provider, { skip: !provider || manual || walletIndex !== 2 })
	const userId = useAppSelector(s => s.auth.user?.uuid),
		navigate = useNavigate()
	const [t] = useTranslation('wallet'),
		{ t: tInputs } = useTranslation('inputs')
	const [customValue, setCustomValue] = useState<number | ''>('')
	const [customSelected, setCustomSelected] = useState(false)
	const [quotes, setQuotes] = useState<Record<number, ManualVcQuote>>({})
	const [error, setError] = useState(''),
		[busy, setBusy] = useState(false)
	const min = manual ? 10 : data?.data.min || 1,
		max = manual ? 5000 : data?.data.max || Infinity
	const valid = (v: number) => Number.isInteger(v) && v >= min && v <= max
	useEffect(() => {
		if (!manual || walletIndex !== 2) return
		let alive = true
		const values = [...new Set([...paymentValues, Number(rechargeAmount), Number(customValue)])].filter(
			v => v >= 10 && v <= 5000 && Number.isInteger(v)
		)
		const timer = setTimeout(
			() =>
				Promise.all(values.map(manualVcApi.quote))
					.then(results => {
						if (alive) {
							setQuotes(Object.fromEntries(results.map(q => [q.amount, q])))
							setError('')
						}
					})
					.catch(e => {
						if (alive) setError(e?.response?.data?.message || t('manual_vc.error'))
					}),
			200
		)
		return () => {
			alive = false
			clearTimeout(timer)
		}
	}, [manual, walletIndex, rechargeAmount, customValue, t])
	useEffect(() => {
		if (walletIndex === 2 && rechargeAmount && !paymentValues.includes(Number(rechargeAmount))) {
			setCustomValue(Number(rechargeAmount))
			setCustomSelected(true)
		}
	}, [walletIndex]) // eslint-disable-line react-hooks/exhaustive-deps
	const total = (value: number) =>
		manual ? quotes[value]?.total : data ? Math.round((data.data.percentage / 100 + 1) * value + data.data.constant) : undefined
	const afterTax = (value: number) => (total(value) !== undefined ? t('taxes_message', { amount: NumberFormatter(total(value)!) }) : '')
	const handleConfirm = async () => {
		if (!provider || !valid(Number(rechargeAmount)) || busy) return
		setBusy(true)
		setError('')
		try {
			if (manual && userId) {
				const row = await prepareManualDraft(userId, Number(rechargeAmount))
				if (row.status === 'draft') setWalletIndex?.(3)
				else {
					closeWallet?.()
					navigate(`${manualVcBase}?request=${row.reference}`)
				}
			} else if (provider.provider !== 'manual_vc') await proccessRecharge(Number(rechargeAmount), provider)
		} catch (e: any) {
			setError(e?.response?.data?.message || t('manual_vc.error'))
		} finally {
			setBusy(false)
		}
	}
	return (
		<TabWrapper>
			<MethodTabBody>
				<StyledButtonsGrid
					value={{ value: customSelected ? 'custom' : String(rechargeAmount) }}
					onChange={value => {
						if (value?.value === 'custom') {
							setCustomSelected(true)
							setRechargeAmount?.(Number(customValue))
						} else if (value?.value) {
							setCustomSelected(false)
							setRechargeAmount?.(Number(value.value))
						}
					}}
				>
					{paymentValues.map(value => (
						<ButtonsGrid.Item key={value} value={{ value: String(value) }}>
							<ButtonGrid>
								<label>{NumberFormatter(value)}</label>
								<Typography fontSize={{ sm: '0.625rem', lg: '0.8rem' }}>{afterTax(value)}</Typography>
							</ButtonGrid>
						</ButtonsGrid.Item>
					))}
					<ButtonsGrid.Item value={{ value: 'custom' }}>
						<ButtonGrid maxWidth="14ch">
							<CustomAmountInput
								aria-label={t('manual_vc.amount')}
								type="number"
								min={min}
								max={Number.isFinite(max) ? max : undefined}
								step="1"
								value={customValue}
								onChange={e => {
									const v = e.target.value === '' ? '' : Number(e.target.value)
									setCustomValue(v)
									setCustomSelected(true)
									setRechargeAmount?.(Number(v))
								}}
							/>
							<Typography fontSize={{ sm: '0.625rem', lg: '0.8rem' }}>
								{customValue !== '' &&
									(valid(Number(customValue)) ? afterTax(Number(customValue)) : tInputs('invalid_amount', { min, max }))}
							</Typography>
						</ButtonGrid>
					</ButtonsGrid.Item>
				</StyledButtonsGrid>
			</MethodTabBody>
			{error && <p role="alert">{error}</p>}
			<RaisedButton
				onClick={handleConfirm}
				disabled={busy || !valid(Number(rechargeAmount)) || (manual && !quotes[Number(rechargeAmount)])}
				width="100%"
			>
				{busy ? t('manual_vc.loading') : t('next')}
			</RaisedButton>
		</TabWrapper>
	)
}
export default ValuesTab

const TabWrapper = styled.div`
	width: 100%;
`

const MethodTabBody = styled(TabBody)`
	margin: 1rem 0;
	padding: 0;
	font-size: 0.8rem;
`
const ButtonGrid = styled.div<LayoutProps>`
	width: 100%;
	border: none;
	background: inherit;
	display: flex;
	flex-direction: column;
	label {
		font-size: 1.3rem;
	}
	span {
		/* font-size: 0.7rem; */
		color: ${props => props.theme.colors.maxBluePurple};
	}
	${layout}
`

const StyledButtonsGrid = styled(ButtonsGrid)`
	grid-template-columns: 1fr;
	${({ theme }) => `${theme.mediaQueries.large}{
		grid-template-columns: repeat(2,1fr);
	
	}`}
`

const CustomAmountInput = styled.input`
	width: 100%;
	min-width: 0;
	box-sizing: border-box;
	border: 1px solid #a6a3c1;
	border-radius: 6px;
	background: #fff;
	color: #3f3d56;
	caret-color: #3f3d56;
	-webkit-text-fill-color: #3f3d56;
	font: inherit;
	font-size: 1rem;
	line-height: 1.5;
	padding: 5px 8px;
	text-align: center;
	&:focus {
		outline: 2px solid ${({ theme }) => theme.colors.independence};
		outline-offset: 2px;
	}
`
